import React, { useState, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';

/**
 * `canal` es el canal por el que SALIÓ el código ('whatsapp' | 'sms'), no una
 * preferencia: lo decide el servidor y lo dice en la respuesta. Aquí NO se
 * asume cuál es el default — cambiarlo es una env del backend, y hardcodearlo
 * en el front haría que un build viejo anunciara el canal equivocado.
 *
 * `otpInfo.sugerirSms` viene en true cuando el envío por WhatsApp falló (número
 * sin WhatsApp, o token/plantilla del lado nuestro). En ese caso la pantalla se
 * queda abierta destacando el cambio de canal: es la única salida del usuario,
 * y antes se le devolvía al formulario sin decirle qué hacer.
 */
export default function OtpCode({
    loading, setShowOTP, submitOtp, reset, setValue, toast, getValues, register, resendCode,
    canal, otpInfo = {}, enviarPorOtroCanal
}) {
    const esWhatsApp = canal === 'whatsapp';
    const sugerirSms = Boolean(otpInfo.sugerirSms);
    const destino = otpInfo.telefonoMascarado || getValues('phone');
    const otroCanal = esWhatsApp
        ? { etiqueta: 'No tengo WhatsApp, enviar por SMS' }
        : { etiqueta: 'Mejor enviar por WhatsApp' };

    const [token, setToken] = useState(Array(6).fill(''));
    const [incompleteFields, setIncompleteFields] = useState(Array(6).fill(false));
    const [timer, setTimer] = useState(60); // Temporizador inicializado en 30 segundos
    const [isResendDisabled, setIsResendDisabled] = useState(true);


    useEffect(() => {
        if (timer > 0) {
            const countdown = setInterval(() => setTimer(prev => prev - 1), 1000);
            return () => clearInterval(countdown);
        } else {
            setIsResendDisabled(false); // Habilita el botón cuando el temporizador llega a 0
        }
    }, [timer]);

    const [resendDelay, setResendDelay] = useState(60); // Tiempo inicial de espera

    const handleResendCode = async () => {
        // reset()
        setToken(Array(6).fill('')); // Limpia los campos de entrada
        setIncompleteFields(Array(6).fill(false)); // Resetea errores visuales

        try {
            const success = await resendCode(); // Llama a la función resendCode
            if (true) {
                setTimer(resendDelay); // Reinicia temporizador
                setIsResendDisabled(true); // Deshabilita botón
                // toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Código reenviado.' });
                setTimeout(() => {
                    setIsResendDisabled(false);
                }, resendDelay * 1000);

                setResendDelay((prevDelay) => Math.min(prevDelay + 50, 200));
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'Error al reenviar el código.' });
                console.error("Error al reenviar el código.");
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Error al reenviar el código.' });
            console.error("Error en la petición de reenviar código:", error);
        }
    };



    const handleChange = (e, index) => {
        const value = e.target.value;
        const newToken = [...token];

        if (value === '' || (/^[0-9]$/.test(value) && value.length === 1)) {
            newToken[index] = value;
            setToken(newToken);

            if (value !== '' && index < token.length - 1) {
                document.getElementById(`otp-input-${index + 1}`).focus();
            }
        }

        setValue(`otp[${index}]`, value);
        
        setIncompleteFields((prev) => {
            const updated = [...prev];
            updated[index] = false;
            return updated;
        });
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace') {
            const newToken = [...token];

            if (token[index] === '' && index > 0) {
                document.getElementById(`otp-input-${index - 1}`).focus();
                newToken[index - 1] = '';
                setToken(newToken);
                setValue(`otp[${index - 1}]`, '');
            } else if (token[index] !== '') {
                newToken[index] = '';
                setToken(newToken);
                setValue(`otp[${index}]`, '');
            }
            setIncompleteFields(Array(6).fill(false));
        } else if (e.key === 'Enter' && index === token.length - 1) {
            e.preventDefault(); // Previene comportamiento por defecto
            handleSubmit(e); // Ejecuta la función de envío
        }
    };

    const validateToken = () => {
        const incomplete = token.map((value) => value === '');
        setIncompleteFields(incomplete);
        return incomplete.every((field) => field === false);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateToken()) {
            submitOtp(token.join(''));
        }
    };


    const renderInput = (id) => (
        <InputText
            key={id}
            id={`otp-input-${id}`}
            type="text"
            className={`custom-otp-input ${incompleteFields[id] ? 'error' : ''}`}
            maxLength="1"
            value={token[id]}
            onChange={(e) => handleChange(e, id)}
            onKeyDown={(e) => handleKeyDown(e, id)}
            name={`otp[${id}]`}
            
            // onBlur={() => otpFormik.setFieldTouched(`otp[${id}]`, false)}
        />
    );

    return (
        <div
    style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        margin: 'auto',
    }}
>
    <form onSubmit={handleSubmit}>
        <div className="card flex justify-center">
            <style scoped>
                {`
                .custom-otp-input {
                    width: 48px;
                    height: 48px;
                    font-size: 24px;
                    text-align: center;
                    transition: all 0.2s;
                    border-radius: 6px;
                    border: 1px solid black;
                    background: transparent;
                    color: #34b9e0;
                    margin: 0 4px;
                }
                .custom-otp-input:focus {
                    outline: 2px solid #34b9e0;
                }
                .custom-otp-input.error {
                    border-color: red;
                }
                .separator {
                    font-size: 24px;
                    color: #34b9e0;
                    margin: 0 8px;
                }
                .text-color-secondary {
                    color: #34b9e0;
                }
                .btn-primary {
                    background-color: #34b9e0;
                    border: none;
                    color: white;
                }
                .btn-primary:hover {
                    background-color: #2aa6c8;
                }
                `}
            </style>
            <div
                className="flex flex-column align-items-center"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    textAlign: 'center',
                }}
            >
                <p className="font-bold text-xl mb-2">Verifica tu número</p>
                {sugerirSms ? (
                    // El texto viene del backend porque solo él sabe si falló
                    // el número o la configuración: hardcodear "no tiene
                    // WhatsApp" culpaba al cliente de un token caducado.
                    // El sufijo "pídelo por SMS" ya no se concatena aquí: el
                    // canal de escape depende del default del servidor y el
                    // botón de abajo ya lo nombra.
                    <p className="block mb-5 text-center" style={{ color: '#b3261e' }}>
                        {otpInfo.mensajeFallo || `No pudimos enviar el código a ${destino}.`}
                    </p>
                ) : (
                    <>
                        <p className="text-color-secondary block mb-5 text-center">
                            {otpInfo.cayoASms && 'No pudimos usar WhatsApp. '}
                            Te enviamos un código por <strong>{esWhatsApp ? 'WhatsApp' : 'SMS'}</strong> a{' '}
                            <strong>{destino}</strong>
                        </p>
                        <p className="text-color-secondary block mb-5 text-center text-[12px] text-gray-800">
                            este puede tardar unos minutos en llegar.
                        </p>
                    </>
                )}

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {Array.from({ length: 6 }).map((_, i) => (
                        <React.Fragment key={i}>
                            {renderInput(i)}
                            {i === 2 && <span className="separator">-</span>}
                        </React.Fragment>
                    ))}
                </div>
                <div className="flex justify-center mt-5 w-12 h-10 gap-5 items-end"
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '1rem',
                        height: '3rem',
                    }}
                >
                    <Button
                        label="← Regresar"
                        // style={{ marginTop: '1rem' }}
                        onClick={(e) => {
                            e.preventDefault(); // Previene el envío del formulario
                            setShowOTP(false);
                        }}
                        link
                        style={{
                            width: '8em',
                            color: '#7e8490',
                        }}
                        className="p-0 text-[#34b9e0] h-16 w-[3em]"
                    />
                 
                    <Button
                    label='Verificar'
                        type="submit"
                        className="Button Green w-1/2"
                       
                    />
                </div>
            </div>
        </div>
    </form>
    <Button
        label={isResendDisabled ? `Reenviar código en ${timer}s` : 'Reenviar código'}
        disabled={isResendDisabled}
        onClick={(e) => {
            // e.preventDefault(); // Previene el envío del formulario
            handleResendCode();
        }}
        link
        style={{
            fontSize: '12px',
            color: '#7e8490',
        }}
        className="p-0 font-[8px] mt-4"
    />

    {/* Cambio al otro canal. Se ofrece en ambos sentidos: el default lo decide
        el backend, así que la pantalla no puede asumir que el escape es siempre
        "hacia SMS".
        NO respeta el temporizador cuando el envío falló: ahí no hay ningún
        código en camino que esperar, y el backend tiene su propio cooldown. */}
    {enviarPorOtroCanal && (
        <Button
            label={otroCanal.etiqueta}
            disabled={!sugerirSms && isResendDisabled}
            onClick={() => enviarPorOtroCanal()}
            link
            style={{
                fontSize: '12px',
                color: sugerirSms ? '#34b9e0' : '#7e8490',
                fontWeight: sugerirSms ? 600 : 400,
            }}
            className="p-0 mt-2"
        />
    )}
</div>

    );
}



